import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  XAxis, YAxis, CartesianGrid, Tooltip, AreaChart, Area
} from 'recharts';
import {
  TrendingUp, Target, Plus, DollarSign, Calendar, Star, ArrowUp, AlertTriangle
} from 'lucide-react';
import {
  startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  startOfQuarter, endOfQuarter, startOfYear, endOfYear,
  isWithinInterval, parseISO, format, eachWeekOfInterval, startOfDay, endOfDay
} from 'date-fns';
import { useAppStore } from '@/lib/data';
import { useActiveWorkspace } from '@/hooks/use-active-workspace';
import { formatCurrency } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BudgetAdjustModal } from '@/components/budgets/budget-adjust-modal';
import { AddCategorySheet } from '@/components/budgets/add-category-sheet';

import * as LucideIcons from 'lucide-react';

// ─── Helpers ─────────────────────────────────────────────────────────────────
function resolveIcon(icon: any): React.FC<{ className?: string }> {
  if (!icon) return Target as any;
  if (typeof icon === 'string') {
    return ((LucideIcons as any)[icon] ?? Target) as any;
  }
  return icon as any;
}
function getDateRange(timeframe: string) {
  const now = new Date();
  switch (timeframe) {
    case 'week':    return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
    case 'quarter': return { start: startOfQuarter(now), end: endOfQuarter(now) };
    case 'year':    return { start: startOfYear(now), end: endOfYear(now) };
    default:        return { start: startOfMonth(now), end: endOfMonth(now) };
  }
}

// ─── Sub-components ───────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload?.length) {
    return (
      <div className="bg-white dark:bg-slate-900 p-3 border rounded-xl shadow-xl border-border">
        <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">{payload[0].payload.name}</p>
        <p className="text-sm font-black text-primary">{formatCurrency(payload[0].value)}</p>
      </div>
    );
  }
  return null;
};

const CategoryCard = ({ category, onClick }: { category: any; onClick: () => void }) => {
  const ratio = category.budgetAmount > 0 ? category.spentAmount / category.budgetAmount : 0;
  const IconComponent = resolveIcon(category.icon);
  return (
    <Card
      className="hover:ring-2 ring-primary transition-all cursor-pointer border-none shadow-md overflow-hidden group bg-card"
      onClick={onClick}
    >
      <CardContent className="p-5">
        <div className="flex justify-between items-start mb-4">
          <div className="p-3 rounded-2xl bg-slate-100 dark:bg-slate-800 text-primary group-hover:scale-110 transition-transform">
            <IconComponent className="h-6 w-6" />
          </div>
          <Badge
            variant={ratio > 1 ? 'destructive' : 'secondary'}
            className={`rounded-md ${ratio > 0.8 && ratio <= 1 ? 'bg-amber-100 text-amber-700 border-amber-200' : ''}`}
          >
            {ratio > 1 ? <AlertTriangle className="h-3 w-3 mr-1" /> : <ArrowUp className="h-3 w-3 mr-1" />}
            {Math.round(ratio * 100)}%
          </Badge>
        </div>
        <h4 className="font-bold text-slate-800 dark:text-slate-100">{category.name}</h4>
        <div className="mt-4 flex justify-between text-xs mb-1">
          <span className="text-muted-foreground font-medium">Used {formatCurrency(category.spentAmount)}</span>
          <span className="font-bold">{formatCurrency(category.budgetAmount)}</span>
        </div>
        <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${ratio > 1 ? 'bg-destructive' : ratio > 0.8 ? 'bg-amber-500' : 'bg-primary'}`}
            style={{ width: `${Math.min(100, ratio * 100)}%` }}
          />
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const BudgetDashboard = () => {
  const { activeWorkspace } = useActiveWorkspace();
  const { transactions, categories } = useAppStore();
  const { toast } = useToast();

  const [selectedTimeframe, setSelectedTimeframe] = useState('month');
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddCategorySheetOpen, setIsAddCategorySheetOpen] = useState(false);

  // Track which categories have already triggered alerts this session
  const alertedRef = useRef<Set<string>>(new Set());

  // ── Data processing with real date filtering ──────────────────────────────
  const { budgetData, totalBudget, totalSpent, chartData } = useMemo(() => {
    const { start, end } = getDateRange(selectedTimeframe);
    const workspaceCats = categories.filter(c => c.workspace === activeWorkspace);

    // Filter transactions to the selected timeframe
    const filteredTrans = transactions.filter(t => {
      if (t.workspace !== activeWorkspace) return false;
      try {
        return isWithinInterval(parseISO(t.date), { start, end });
      } catch { return false; }
    });

    const data = workspaceCats
      .filter(c => c.type === 'expense' && c.budget)
      .map(cat => {
        const spent = filteredTrans
          .filter(t => t.category_id === cat.id || t.category === cat.name)
          .reduce((sum, t) => sum + Math.abs(t.amount), 0);
        return { ...cat, spentAmount: spent, budgetAmount: cat.budget || 0 };
      });

    // Real weekly chart: group filtered transactions by week
    const weeks = eachWeekOfInterval({ start, end }, { weekStartsOn: 1 });
    const chart = weeks.map((weekStart, i) => {
      const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
      const weekSpent = filteredTrans
        .filter(t => {
          try { return isWithinInterval(parseISO(t.date), { start: weekStart, end: weekEnd }); }
          catch { return false; }
        })
        .reduce((sum, t) => sum + Math.abs(t.amount), 0);
      return { name: `Wk ${i + 1}`, spent: weekSpent };
    });

    return {
      budgetData: data,
      totalBudget: data.reduce((s, c) => s + c.budgetAmount, 0),
      totalSpent: data.reduce((s, c) => s + c.spentAmount, 0),
      chartData: chart,
    };
  }, [transactions, categories, activeWorkspace, selectedTimeframe]);

  const totalRemaining = totalBudget - totalSpent;
  const burnRate = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;

  // ── Overspend alerts ──────────────────────────────────────────────────────
  useEffect(() => {
    budgetData.forEach(cat => {
      const ratio = cat.budgetAmount > 0 ? cat.spentAmount / cat.budgetAmount : 0;
      const key100 = `${cat.id}-100`;
      const key80 = `${cat.id}-80`;

      if (ratio >= 1 && !alertedRef.current.has(key100)) {
        alertedRef.current.add(key100);
        toast({
          title: `⚠️ Budget exceeded: ${cat.name}`,
          description: `You've spent ${formatCurrency(cat.spentAmount)} of your ${formatCurrency(cat.budgetAmount)} budget.`,
          variant: 'destructive',
        });
      } else if (ratio >= 0.8 && ratio < 1 && !alertedRef.current.has(key80)) {
        alertedRef.current.add(key80);
        toast({
          title: `🔔 Approaching limit: ${cat.name}`,
          description: `${Math.round(ratio * 100)}% used — ${formatCurrency(cat.budgetAmount - cat.spentAmount)} remaining.`,
        });
      }
    });
  }, [budgetData, toast]);

  const timeframeLabel: Record<string, string> = {
    week: 'This Week', month: 'This Month', quarter: 'This Quarter', year: 'This Year'
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-slate-950 p-4 md:p-8 space-y-8">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="capitalize bg-background font-bold tracking-tight">
                {activeWorkspace} Workspace
              </Badge>
              {burnRate > 90 && (
                <Badge className="bg-amber-100 text-amber-700 border-amber-200 animate-pulse">
                  Attention Required
                </Badge>
              )}
            </div>
            <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white">Budget Overview</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{timeframeLabel[selectedTimeframe]}</p>
          </div>

          <div className="flex items-center gap-3">
            <Select value={selectedTimeframe} onValueChange={setSelectedTimeframe}>
              <SelectTrigger className="w-[140px] rounded-xl bg-white dark:bg-slate-900 shadow-sm border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="quarter">This Quarter</SelectItem>
                <SelectItem value="year">This Year</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => setIsAddCategorySheetOpen(true)} className="rounded-xl shadow-lg shadow-primary/20">
              <Plus className="h-4 w-4 mr-2" /> Add Category
            </Button>
          </div>
        </div>

        {/* Hero Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <Card className="lg:col-span-3 border-none shadow-2xl bg-slate-900 text-white overflow-hidden relative">
            <div className="absolute top-0 right-0 p-8 opacity-5">
              <DollarSign className="h-40 w-40" />
            </div>
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
                <div className="space-y-1">
                  <p className="text-slate-400 text-xs font-bold uppercase tracking-widest">Budgeted</p>
                  <p className="text-3xl font-bold tracking-tighter">{formatCurrency(totalBudget)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-slate-400 text-xs font-bold uppercase tracking-widest">Spent</p>
                  <p className="text-3xl font-bold tracking-tighter">{formatCurrency(totalSpent)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-slate-400 text-xs font-bold uppercase tracking-widest">Remaining</p>
                  <p className={`text-3xl font-bold tracking-tighter ${totalRemaining < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                    {formatCurrency(totalRemaining)}
                  </p>
                </div>
              </div>
              <div className="mt-10 space-y-2">
                <div className="flex justify-between text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  <span>Spending Progress</span>
                  <span>{Math.round(burnRate)}% utilized</span>
                </div>
                <div className="h-3 w-full bg-white/10 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-1000 ${burnRate > 100 ? 'bg-rose-500' : 'bg-primary'}`}
                    style={{ width: `${Math.min(100, burnRate)}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="flex flex-col items-center justify-center p-6 text-center shadow-xl border-none bg-card">
            <Target className="h-10 w-10 text-primary mb-3" />
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-tighter">Health Score</h3>
            <div className="text-5xl font-black my-2 text-foreground">
              {Math.max(0, Math.round(100 - (burnRate > 100 ? 100 : burnRate / 1.5)))}%
            </div>
            <p className="text-[10px] text-muted-foreground uppercase leading-tight font-medium">Efficiency based on limits</p>
          </Card>
        </div>

        {/* Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-none shadow-xl bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" /> Weekly Spending
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorSpent" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                  <YAxis hide />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="spent" stroke="hsl(var(--primary))" fillOpacity={1} fill="url(#colorSpent)" strokeWidth={3} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="border-none shadow-xl bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Star className="h-4 w-4 text-amber-500" /> Distribution
              </CardTitle>
            </CardHeader>
            <CardContent className="flex items-center h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={budgetData} innerRadius={60} outerRadius={85} paddingAngle={5} dataKey="spentAmount">
                    {budgetData.map((entry, i) => (
                      <Cell key={i} fill={entry.color || 'hsl(var(--primary))'} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="hidden md:flex flex-col gap-3 ml-4 w-48">
                {budgetData.slice(0, 4).map(cat => (
                  <div key={cat.id} className="flex flex-col">
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase text-muted-foreground">
                      <div className="w-2 h-2 rounded-full" style={{ background: cat.color }} />
                      <span className="truncate">{cat.name}</span>
                    </div>
                    <span className="text-xs font-bold pl-4">
                      {Math.round((cat.spentAmount / (totalSpent || 1)) * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Categories Grid */}
        <div className="space-y-4">
          <h2 className="text-xl font-black tracking-tight px-1 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" /> Active Budgets
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {budgetData.map(cat => (
              <CategoryCard
                key={cat.id}
                category={cat}
                onClick={() => { setSelectedCategory(cat); setIsModalOpen(true); }}
              />
            ))}
            {budgetData.length === 0 && (
              <div className="col-span-full py-12 text-center bg-white dark:bg-slate-900 rounded-3xl border-2 border-dashed">
                <p className="text-muted-foreground font-medium">No expense categories with budgets found.</p>
                <Button variant="link" onClick={() => setIsAddCategorySheetOpen(true)}>Create your first budget</Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <BudgetAdjustModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        category={selectedCategory}
        transactions={transactions}
      />
      <AddCategorySheet
        open={isAddCategorySheetOpen}
        onOpenChange={setIsAddCategorySheetOpen}
      />
    </div>
  );
};

export default BudgetDashboard;
